<template lang="html">
  <div class="grid-x grid-margin-x">
    <div class="small-12 cell">
      <label class="has-float-label">
        <input
          v-model="value"
          :type="type"
          :name="name"
          :placeholder="placeholder"
          :class="{ fail: failState }"
          @input="$emit('input', $event.target.value)"
          @blur="$emit('blur')"
          disabled="disabled"
        >
        <span>{{ placeholder }}</span>
      </label>
      <span
        v-show="failState"
        :id="name + '-fail'"
        class="fail-icon"
      >
        <div class="icon-container icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            class="icon vertical-align-middle"
          >
            <g
              fill="none"
              fill-rule="evenodd"
              transform="translate(4, -3)"
            >
              <path d="M0 0h24v24H0z" />
              <path
                fill="#FF495C"
                d="M3.5 19h17L12 4 3.5 19zm9.5-2h-2v-2h2v2zm0-3h-2v-4h2v4z"
              />
            </g>
          </svg>
        </div>
      </span>
      <span
        v-show="passState"
        :id="name + '-pass'"
        class="validation-icon icon"
      >
        <div class="icon-container">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
          >
            <g
              fill="none"
              fill-rule="evenodd"
              transform="translate(2, 0)"
            >
              <path d="M0 0h24v24H0z" />
              <!-- eslint-disable-next-line -->
                  <path fill="#9CD04B" d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-2-4.58l9-9L17.59 7 10 14.59l-3.59-3.58L5 12.42l5 5z"/>
            </g>
          </svg>
        </div>
      </span>
    </div>
  </div>
</template>

<script>

export default {
  props: {
    name: {
      type: String,
      required: true,
    },
    defaultValue: {
      type: String,
      required: false,
      default: '',
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    type: {
      type: String,
      required: false,
      default: '',
    },
    failState: {
      type: Boolean,
      required: false,
      default: false,
    },
    passState: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      value: '',
      iconStyle: '',
      fieldName: this.name,
    };
  },
  mounted() {
    this.value = this.defaultValue;
    /* --- Dynamic positioning of validation icon --- */
    /* When the component mounts, calculate the width of the input field, which is dynamic... */
    const field = document.querySelector(`input[name="${this.name}"]`);
    const fieldWidth = field.offsetWidth;

    /*

      ...now calculate and set desired position of the icon relative to the left of the field.

      first we detect the browser: the position fix is only needed for pass icon in Edge and IE.

      this is likely due to the way they render v-show...

      browser detection courtesy of this Stack thread: https://stackoverflow.com/questions/9847580/how-to-detect-safari-chrome-ie-firefox-and-opera-browser

      */

    const isIE = /* @cc_on!@ */ false || !!document.documentMode;
    const isEdge = !isIE && !!window.StyleMedia;
    const iconPosition = (fieldWidth / 16) - 2;

    if (!isIE && !isEdge) {
      document.getElementById(`${this.name}-fail`).style.left = `${iconPosition}rem`;
      document.getElementById(`${this.name}-pass`).style.left = `${iconPosition}rem`;
    } else {
      document.getElementById(`${this.name}-pass`).style.left = `${iconPosition}rem`;
    }

    /* handle clearing the field from the parent */
    this.$on('clearField', () => {
      this.value = '';
    });
  },
};

</script>

<style lang="scss" scoped>

  $grid: 12px;

  /* - Colour - */

  $secondary-color: #c4cace;
  $primary-accent-color: #50e3c2;
  $main-text-color: #303030;
  $blur-text-color: #7d8b96;

  @mixin float-label-container {
    display: block;
    position: relative;
  }

  @mixin float-label {
    label, > span {
      color: $blur-text-color;
      cursor: text;
      font-size: 75%;
      left: 0.5rem;
      letter-spacing: 0.2px;
      line-height: 1.5;
      opacity: 1;
      position: absolute;
      top: -0.75rem;
      transition: all .2s;
      @content;
    }
  }

  @mixin float-label-input {
    &::placeholder {
      opacity: 1;
      transition: all .2s;
    }
    &:placeholder-shown:not(:focus)::placeholder {
      opacity: 0;
    }
  }

  @mixin float-label-scaled {
    &:placeholder-shown:not(:focus) + * {
      font-size: 1rem !important;
      letter-spacing: 0.2px;
      opacity: .5;
      @content;
    }
  }

  .has-float-label {
    @include float-label-container;

    @include float-label;

    input {
      @include float-label-input;
      background-color: transparent;
      border-style: none none solid none !important;
      border-radius: 1px !important;
      box-shadow: none !important;
      color: $main-text-color !important;
      font-size: inherit !important;
      font-weight: bold;
      left: 0.375rem;
      letter-spacing: 0.5px;
      padding-top: 1rem !important;
      margin-bottom: 2px !important;

      @include float-label-scaled {
        left: 1rem;
        top: 0.5rem !important;
      }

      &:focus {
        outline: none !important;
        border-bottom: 2px solid $primary-accent-color !important;
      }

      &.fail {
        outline: none !important;
        border-bottom: 2px solid #ff495c !important;
      }

      &:-ms-input-placeholder {
        color: $secondary-color !important;
      }
    }
  }

  .fail-icon {
    color: #ff495c;
    line-height: 24px;
    font-size: 12px;
    position: relative;
    text-align: left;
    top: -30px;
    z-index: -1;
  }

  .validation-icon {
    line-height: 24px;
    position: relative;
    top: -36px;
  }

</style>
